import React from 'react';
import { Link } from 'react-router-dom';
import { Page } from './StaticPages';

const UPDATED = 'Last updated: June 2026';
const EMAIL = 'info@scholarindustry.com';
const A = ({ to, children }) => <Link to={to} className="text-[var(--si-blue)] font-semibold underline">{children}</Link>;
const Updated = () => <p className="text-sm text-[var(--si-grey)]">{UPDATED}</p>;

const Section = ({ n, title, children }) => (
  <section className="space-y-2">
    <h3>{n}. {title}</h3>
    {children}
  </section>
);

const List = ({ items }) => <ul className="list-disc pl-5 space-y-1">{items.map((t) => <li key={t}>{t}</li>)}</ul>;

export const Faq = () => (
  <Page eyebrow="Support" title="Frequently asked questions">
    <h3>Are there any extra charges at checkout?</h3>
    <p>No. All prices are inclusive of GST. We do not add a platform fee or a payment processing fee on top. The only extra is a ₹49 delivery charge on orders below ₹799.</p>
    <h3>Is delivery free?</h3>
    <p>Yes. Delivery is free all over India on orders of ₹799 and above. Orders below ₹799 have a flat ₹49 delivery charge.</p>
    <h3>How do the Buy 1 Get 1 and Buy 2 Get 1 offers work?</h3>
    <p>Add any two ₹999 products and one is free (Buy 1 Get 1 Free). Add any three ₹3,999 products and one is free (Buy 2 Get 1 Free). The discount is applied automatically in your cart.</p>
    <h3>Can I return a product?</h3>
    <p>We offer a <strong>7-day no-questions-asked replacement</strong> (size, colour or the same product). We do not accept returns for refunds, except when we have delivered a wrong or damaged product. See our <A to="/returns">replacement policy</A>.</p>
    <h3>What sizes do you carry?</h3>
    <p>T-shirts, kurtas and punjabis come in S–XXL. Kurtis come in XS–XL. Sarees are free size and include an unstitched blouse piece. See the <A to="/size-guide">size guide</A>.</p>
    <h3>Are the Limited Edition pieces really limited?</h3>
    <p>Yes. Each ₹15,999 hand-stitched piece is made in a run of 25 and is not remade once sold out.</p>
  </Page>
);

export const Shipping = () => (
  <Page eyebrow="Shipping" title="Shipping & delivery policy">
    <Updated />
    <Section n={1} title="Delivery charges">
      <List items={['FREE delivery all over India on orders of ₹799 and above.', 'Flat ₹49 delivery charge on orders below ₹799.', 'All prices include GST. No platform fee or processing fee is added to any order.']} />
    </Section>
    <Section n={2} title="Processing time">
      <p>Orders are packed and dispatched within 1–2 business days of successful payment. Hand-stitched Limited Edition pieces may take up to 5 business days to dispatch.</p>
    </Section>
    <Section n={3} title="Delivery time">
      <List items={['Metro cities: 3–5 business days after dispatch', 'Rest of India: 5–8 business days after dispatch', 'Remote areas and North-East: up to 10 business days']} />
    </Section>
    <Section n={4} title="Tracking">
      <p>Once your order is shipped, we will share the courier name and tracking details on your registered email or phone number.</p>
    </Section>
    <Section n={5} title="Delivery issues">
      <p>If your package arrives opened, tampered or damaged, please record an unboxing video and contact us at <strong>{EMAIL}</strong> within 48 hours of delivery.</p>
    </Section>
  </Page>
);

export const Returns = () => (
  <Page eyebrow="Replacement" title="7-day replacement policy">
    <Updated />
    <p className="p-3 rounded bg-[#E8F5E9] font-semibold text-[var(--si-green)]">7-day no-questions-asked replacement. Returns for refund are not accepted, except when we deliver a wrong product.</p>
    <Section n={1} title="Replacement window">
      <p>You can request a replacement within <strong>7 days of delivery</strong>, for any reason. No questions asked. You may choose a different size, a different colour, or the same product.</p>
    </Section>
    <Section n={2} title="Conditions">
      <List items={['The product must be unused, unwashed and unaltered.', 'Original tags, packaging and invoice must be intact.', 'Replacement is subject to stock availability. If the requested size or colour is out of stock, you may choose another product of equal value.', 'For Buy 1 Get 1 / Buy 2 Get 1 orders, the free item must be sent back along with the item being replaced.']} />
    </Section>
    <Section n={3} title="No returns for refund">
      <p>We do not accept returns for refund for change of mind, fit or preference. A replacement is offered instead.</p>
    </Section>
    <Section n={4} title="Wrong or damaged product: full refund">
      <p>If we deliver a wrong product (a different item, size or colour from what you ordered) or a damaged or defective product, you can choose a free replacement or a <strong>full refund</strong>. Please report it within 48 hours of delivery with photos or an unboxing video. Refunds are credited to the original payment method within 5–7 business days after we receive the item.</p>
    </Section>
    <Section n={5} title="How to request">
      <p>Email <strong>{EMAIL}</strong> with your order ID, the item and the size or colour you want. We will arrange a pickup where available, or share the return address.</p>
    </Section>
  </Page>
);

export const Privacy = () => (
  <Page eyebrow="Legal" title="Privacy policy">
    <Updated />
    <p>Scholar Industry ("we", "us") respects your privacy. This policy explains what information we collect on scholarindustry.com, how we use it and your choices.</p>
    <Section n={1} title="Information we collect">
      <List items={['Account details: name, email, phone number and password (stored in encrypted form).', 'Order details: shipping address, items purchased and order history.', 'Payment details: payments are processed by Razorpay. We never see or store your card, UPI or bank details.', 'Usage data: pages visited, device and browser type, used to improve the website.']} />
    </Section>
    <Section n={2} title="How we use your information">
      <List items={['To process, ship and deliver your orders.', 'To handle replacement and refund requests.', 'To send order updates and, if you subscribe, offers and new collection news.', 'To prevent fraud and keep the website secure.']} />
    </Section>
    <Section n={3} title="Sharing">
      <p>We do not sell or rent your personal data. We share only what is needed with trusted partners: payment gateway (Razorpay), courier partners for delivery, and email service providers, or when required by law.</p>
    </Section>
    <Section n={4} title="Cookies">
      <p>We use local storage and cookies to keep you logged in, remember your cart and wishlist, and understand site usage. You can clear them from your browser at any time.</p>
    </Section>
    <Section n={5} title="Data security & retention">
      <p>We use industry-standard security measures. We keep your data only as long as needed for orders, legal and accounting requirements.</p>
    </Section>
    <Section n={6} title="Your rights">
      <p>You can request access, correction or deletion of your personal data, or unsubscribe from marketing emails, by writing to <strong>{EMAIL}</strong>.</p>
    </Section>
    <Section n={7} title="Changes">
      <p>We may update this policy from time to time. The latest version will always be available on this page.</p>
    </Section>
  </Page>
);

export const Terms = () => (
  <Page eyebrow="Legal" title="Terms & conditions">
    <Updated />
    <p>By using scholarindustry.com or placing an order, you agree to these terms.</p>
    <Section n={1} title="Eligibility & account">
      <p>You must be 18 or older, or use the site under a parent's supervision. You are responsible for keeping your login details safe.</p>
    </Section>
    <Section n={2} title="Products & pricing">
      <List items={['All prices are in Indian Rupees (₹). Prices shown are inclusive of GST. No platform fee or processing fee is added.', 'A ₹49 delivery charge applies to orders below ₹799. Delivery is free on ₹799 and above.', 'MRP and discount percentages are shown for reference during the sale.', 'Product images are representative. Handcrafted and hand-stitched pieces may vary slightly in colour and pattern, which is not a defect.', 'Prices, offers and stock may change without notice. Out-of-stock items cannot be ordered.']} />
    </Section>
    <Section n={3} title="Offers">
      <List items={['Buy 1 Get 1 Free applies to ₹999 products. Buy 2 Get 1 Free applies to ₹3,999 products.', 'The lowest-priced eligible item is free. Offers are applied automatically in the cart and cannot be exchanged for cash.', 'We may change or end any offer at any time.']} />
    </Section>
    <Section n={4} title="Orders & payment">
      <p>An order is confirmed only after successful payment through Razorpay. We may cancel an order because of stock, pricing errors or suspected fraud. In that case the full amount is refunded.</p>
    </Section>
    <Section n={5} title="Replacement & refund">
      <p>We offer a 7-day no-questions-asked replacement. Returns for refund are not accepted, except when we deliver a wrong, damaged or defective product. See the <A to="/returns">replacement policy</A>.</p>
    </Section>
    <Section n={6} title="Intellectual property">
      <p>All designs, artwork, logos, text and images on this website belong to Scholar Industry and may not be copied or reused without written permission.</p>
    </Section>
    <Section n={7} title="Limitation of liability">
      <p>Our liability for any order is limited to the amount paid for that order.</p>
    </Section>
    <Section n={8} title="Governing law">
      <p>These terms are governed by the laws of India. Disputes are subject to the jurisdiction of courts in Kolkata, West Bengal.</p>
    </Section>
    <Section n={9} title="Contact">
      <p>Questions about these terms? Write to <strong>{EMAIL}</strong>.</p>
    </Section>
  </Page>
);

export const CancellationRefund = () => (
  <Page eyebrow="Legal" title="Cancellation & refund policy">
    <Updated />
    <Section n={1} title="Cancellation">
      <List items={['You can cancel an order before it is shipped by emailing your order ID to ' + EMAIL + '.', 'Once shipped, an order cannot be cancelled. You can use the 7-day replacement instead.', 'We may cancel an order because of stock or payment issues. You will get a full refund.']} />
    </Section>
    <Section n={2} title="When you get a refund">
      <List items={['Order cancelled before shipping.', 'Wrong product delivered (different item, size or colour from your order).', 'Damaged or defective product, reported within 48 hours of delivery.', 'Payment deducted but order not confirmed.']} />
    </Section>
    <Section n={3} title="When refunds are not given">
      <p>Change of mind, fit or preference. For these, we offer a free <A to="/returns">7-day replacement</A>.</p>
    </Section>
    <Section n={4} title="Refund timeline">
      <p>Approved refunds are credited to your original payment method within 5–7 business days. Your bank may take a few extra days to show it.</p>
    </Section>
  </Page>
);
